import { useState } from 'react';
import {
  Network, Users, TrendingUp, Building2, Star, MapPin,
  Link, ChevronRight, Plus, Search, Shield, Globe, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const INCUBATORS = [
  { id: 1, name: 'BIADA Innovation Hub', location: 'Patna, Bihar', type: 'Govt. Incubator', startups: 48, status: 'connected', specialty: 'AgriTech, FinTech' },
  { id: 2, name: 'T-Hub Hyderabad', location: 'Hyderabad, TS', type: 'Private Incubator', startups: 320, status: 'available', specialty: 'DeepTech, AI/ML' },
  { id: 3, name: 'IIT Patna TIDES', location: 'Patna, Bihar', type: 'University', startups: 72, status: 'available', specialty: 'EdTech, HealthTech' },
];

const INVESTORS = [
  { id: 1, name: 'Prashant Capital', type: 'Angel', focus: 'AI/ML, SaaS', ticket: '₹10L-1Cr', portfolio: 28, location: 'Bangalore', available: true },
  { id: 2, name: 'Bihar Ventures Fund', type: 'VC', focus: 'Bihar Startups', ticket: '₹50L-5Cr', portfolio: 15, location: 'Patna', available: true },
  { id: 3, name: 'Sequoia Seed', type: 'VC', focus: 'B2B, Deep Tech', ticket: '$500K-2M', portfolio: 120, location: 'Mumbai', available: false },
  { id: 4, name: 'Accel Partners', type: 'VC', focus: 'Consumer, SaaS', ticket: '$1M-5M', portfolio: 200, location: 'Bangalore', available: false },
];

const MENTORS = [
  { id: 1, name: 'Dr. Alok Sharma', expertise: 'AI & Product Strategy', exp: '15 yrs', sessions: 42, rating: 4.9, available: true },
  { id: 2, name: 'Nisha Gupta', expertise: 'Go-to-Market, Growth', exp: '12 yrs', sessions: 28, rating: 4.8, available: true },
  { id: 3, name: 'Rajiv Mehta', expertise: 'Fundraising & VC Relations', exp: '20 yrs', sessions: 67, rating: 4.7, available: false },
  { id: 4, name: 'Priya Chatterjee', expertise: 'HR & Culture Building', exp: '10 yrs', sessions: 31, rating: 4.9, available: true },
];

export default function Partners() {
  const [search, setSearch] = useState('');

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">PartnerNexus</h1>
          <p className="text-muted-foreground text-sm mt-1">Connect with incubators, discover investors, and access mentors</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="badge-info">2/2 connections used (Free)</span>
          <Button size="sm" className="gradient-primary text-primary-foreground border-0">Upgrade</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Connected Incubators', value: '1', icon: Building2, color: 'text-chart-1', bg: 'bg-chart-1/10' },
          { label: 'Investors Discovered', value: '4', icon: TrendingUp, color: 'text-chart-4', bg: 'bg-chart-4/10' },
          { label: 'Mentor Sessions', value: '8', icon: Users, color: 'text-chart-3', bg: 'bg-chart-3/10' },
          { label: 'Network Score', value: '72/100', icon: Network, color: 'text-primary', bg: 'bg-primary/10' },
        ].map(stat => (
          <Card key={stat.label} className="border-border">
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${stat.bg}`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-xl font-bold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="incubators">
        <TabsList className="bg-muted">
          <TabsTrigger value="incubators">Incubators</TabsTrigger>
          <TabsTrigger value="investors">Investors</TabsTrigger>
          <TabsTrigger value="mentors">Mentors</TabsTrigger>
        </TabsList>

        {/* Incubators */}
        <TabsContent value="incubators" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INCUBATORS.map(inc => (
              <Card key={inc.id} className="border-border hover:shadow-card transition-shadow flex flex-col">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-chart-1/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-chart-1" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm text-foreground">{inc.name}</h3>
                      <p className="text-xs text-muted-foreground">{inc.type}</p>
                    </div>
                    {inc.status === 'connected' && <span className="ml-auto badge-success">Connected</span>}
                  </div>
                  <div className="space-y-1.5 mb-4 flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3" />{inc.location}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" />{inc.startups} startups
                    </div>
                    <div className="text-xs text-muted-foreground">Focus: {inc.specialty}</div>
                  </div>
                  <Button
                    size="sm"
                    className={cn('w-full mt-auto text-xs', inc.status === 'connected'
                      ? 'border-border'
                      : 'gradient-primary text-primary-foreground border-0'
                    )}
                    variant={inc.status === 'connected' ? 'outline' : 'default'}
                    onClick={() => inc.status !== 'connected' && toast.success(`Connection request sent to ${inc.name}`)}
                  >
                    {inc.status === 'connected' ? 'View Partnership' : 'Request Link'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Investors */}
        <TabsContent value="investors" className="mt-6">
          <div className="mb-4 p-4 rounded-xl bg-muted/50 border border-border text-sm">
            <p className="text-muted-foreground">Investor discovery is <span className="text-foreground font-medium">read-only</span> on the Free plan. Upgrade to Plus for full profile + direct connect.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INVESTORS.map(inv => (
              <Card key={inv.id} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-chart-4/10 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-chart-4" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-foreground">{inv.name}</h3>
                      <span className="badge-muted text-xs">{inv.type}</span>
                    </div>
                    <span className={inv.available ? 'badge-success' : 'badge-muted'}>{inv.available ? 'Active' : 'Closed'}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
                    <div><span className="text-foreground font-medium">Focus:</span> {inv.focus}</div>
                    <div><span className="text-foreground font-medium">Ticket:</span> {inv.ticket}</div>
                    <div><span className="text-foreground font-medium">Portfolio:</span> {inv.portfolio} cos</div>
                    <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{inv.location}</div>
                  </div>
                  <Button size="sm" variant="outline" className="w-full border-border text-xs" disabled={!inv.available}>
                    {inv.available ? 'View Profile' : 'Not Accepting'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Mentors */}
        <TabsContent value="mentors" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {MENTORS.map(mentor => (
              <Card key={mentor.id} className="border-border flex flex-col">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                      {mentor.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-foreground">{mentor.name}</h3>
                      <p className="text-xs text-muted-foreground">{mentor.expertise}</p>
                    </div>
                    <span className={mentor.available ? 'badge-success' : 'badge-muted'}>{mentor.available ? 'Available' : 'Busy'}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 flex-1">
                    <span>{mentor.exp} exp</span>
                    <span>{mentor.sessions} sessions</span>
                    <span className="flex items-center gap-0.5"><Star className="w-3 h-3 fill-warning text-warning" />{mentor.rating}</span>
                  </div>
                  <Button
                    size="sm"
                    className={cn('w-full mt-auto text-xs', mentor.available
                      ? 'gradient-primary text-primary-foreground border-0'
                      : 'border-border'
                    )}
                    variant={mentor.available ? 'default' : 'outline'}
                    disabled={!mentor.available}
                    onClick={() => toast.success(`Session request sent to ${mentor.name}`)}
                  >
                    {mentor.available ? 'Book Session' : 'Not Available'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
